import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchProducts } from '../api/productsApi';
import { API_BASE } from '../config/api';
import ProductCard from '../components/ProductCard';
import useIsMobile from '../utils/useIsMobile';

const Products = () => {
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const [items, setItems] = useState([]);
  const [dbCategories, setDbCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (location.state?.category) {
      setActiveCategory(location.state.category);
    }
  }, [location.state]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const [prodData, catRes] = await Promise.all([
          fetchProducts({ active: true }),
          fetch(`${API_BASE}/categories`)
        ]);
        const catData = await catRes.json();
        
        if (!mounted) return;
        setItems(Array.isArray(prodData) ? prodData : []);
        setDbCategories(Array.isArray(catData) ? catData : []);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load products');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    let result = items;
    
    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter((p) => String(p.title || '').toLowerCase().includes(q));
    }
    
    if (activeCategory !== 'all') {
      result = result.filter((p) => p.category === activeCategory);
    }
    
    return result;
  }, [items, query, activeCategory]);

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: `
        .prod-hero { position: relative; overflow: hidden; background: linear-gradient(180deg, #080c14 0%, #0d1220 40%, #111827 100%); }
        .prod-hero::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse 600px 400px at 50% 0%, rgba(255,215,0,0.07), transparent 70%), radial-gradient(ellipse 300px 300px at 85% 50%, rgba(255,215,0,0.04), transparent 70%); pointer-events: none; }
        .prod-hero-inner { position: relative; z-index: 1; }

        .prod-hero-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,215,0,0.1); border: 1px solid rgba(255,215,0,0.2); border-radius: 50px; padding: 6px 16px; font-size: 13px; color: var(--accent); font-weight: 600; margin-bottom: 16px; animation: prodBadgeIn 0.6s ease; }
        @keyframes prodBadgeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }

        .prod-hero-title { font-size: clamp(2rem, 4vw, 3.5rem); font-weight: 900; color: #fff; margin: 0 0 12px; line-height: 1.2; animation: prodTitleIn 0.7s ease; }
        .prod-hero-title span { background: linear-gradient(135deg, var(--accent), #e6c200); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        @keyframes prodTitleIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

        .prod-hero-subtitle { font-size: clamp(0.9rem, 1.5vw, 1.1rem); color: rgba(255,255,255,0.6); margin: 0 auto 24px; max-width: 500px; line-height: 1.6; animation: prodSubIn 0.8s ease; }
        @keyframes prodSubIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

        .prod-search-wrapper { max-width: 520px; margin: 0 auto 24px; animation: prodSearchIn 0.9s ease; position: relative; }
        @keyframes prodSearchIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        .prod-search-wrapper input { width: 100%; padding: 14px 44px 14px 18px; border-radius: 14px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; font-size: 14px; transition: all 0.3s; box-sizing: border-box; }
        .prod-search-wrapper input:focus { outline: none; border-color: var(--accent); background: rgba(255,255,255,0.1); box-shadow: 0 0 0 3px rgba(255,215,0,0.08); }
        .prod-search-wrapper input::placeholder { color: rgba(255,255,255,0.3); }
        .prod-search-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.3); pointer-events: none; }

        .prod-cat-pills { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; animation: prodPillsIn 1s ease; }
        @keyframes prodPillsIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .prod-cat-pill { padding: 7px 16px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.6); font-size: 13px; cursor: pointer; font-weight: 600; transition: all 0.25s ease; white-space: nowrap; }
        .prod-cat-pill:hover { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); transform: translateY(-2px); }
        .prod-cat-pill.active { background: var(--accent); color: #000; border-color: var(--accent); font-weight: 700; box-shadow: 0 4px 14px rgba(255,215,0,0.25); }

        .prod-hero-stats { display: flex; gap: 32px; justify-content: center; margin-top: 28px; flex-wrap: wrap; animation: prodStatsIn 1.1s ease; }
        @keyframes prodStatsIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .prod-stat { text-align: center; }
        .prod-stat-val { font-size: 1.6rem; font-weight: 800; color: var(--accent); line-height: 1; }
        .prod-stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.4); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }

        .prod-filter-icon { display: none; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; width: 40px !important; height: 40px; cursor: pointer; color: #ccc; transition: all 0.2s ease; padding: 0 !important; flex-shrink: 0; min-width: 40px !important; min-height: 40px !important; }
        .prod-filter-icon:hover { background: rgba(255,255,255,0.15); color: var(--accent); border-color: var(--accent); }

        @media (max-width: 768px) {
          .prod-hero { padding: 20px 0 24px; }
          .prod-hero-title { font-size: 1.8rem; text-align: center; }
          .prod-hero-subtitle { text-align: center; margin-left: auto; margin-right: auto; font-size: 0.85rem; }
          .prod-hero-badge { font-size: 11px; padding: 5px 12px; }
          .prod-hero-stats { gap: 20px; margin-top: 20px; }
          .prod-stat-val { font-size: 1.3rem; }
          .prod-stat-label { font-size: 0.68rem; }
          .prod-search-wrapper { max-width: 100%; }
          .prod-search-wrap { display: flex; align-items: center; gap: 8px; width: 100%; }
          .prod-search-wrap input { flex: 1 !important; width: auto !important; min-width: 0; }
          .prod-filter-icon { display: flex !important; }
          .prod-cat-pills { display: none; }
        }
      ` }} />

      <section className="prod-hero">
        <div className="container prod-hero-inner">
          <div style={{ textAlign: 'center', paddingTop: isMobile ? '10px' : '50px', paddingBottom: isMobile ? '8px' : '30px' }}>
            <div className="prod-hero-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              Premium LED Lighting Solutions
            </div>
            <h1 className="prod-hero-title">Discover Our <span>Products</span></h1>
            <p className="prod-hero-subtitle">Explore our complete range of high-performance LED lighting — engineered for efficiency, built for durability, designed to illuminate every space.</p>

            <div className="prod-search-wrapper">
              <div className="prod-search-wrap">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by product name..."
                />
                <svg className="prod-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                {isMobile && (
                  <button className="prod-filter-icon" onClick={() => setFilterOpen(true)} title="Filter categories">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {!isMobile && (
              <div className="prod-cat-pills">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={'prod-cat-pill' + (activeCategory === 'all' ? ' active' : '')}
                >All</button>
                {dbCategories.slice(0, 7).map((cat, i) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={'prod-cat-pill' + (activeCategory === cat.slug ? ' active' : '')}
                  >{cat.name}</button>
                ))}
              </div>
            )}

            <div className="prod-hero-stats">
              <div className="prod-stat">
                <div className="prod-stat-val">{items.length}+</div>
                <div className="prod-stat-label">Products</div>
              </div>
              <div className="prod-stat">
                <div className="prod-stat-val">{dbCategories.length}+</div>
                <div className="prod-stat-label">Categories</div>
              </div>
              <div className="prod-stat">
                <div className="prod-stat-val">5yr</div>
                <div className="prod-stat-label">Warranty</div>
              </div>
              <div className="prod-stat">
                <div className="prod-stat-val">24/7</div>
                <div className="prod-stat-label">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {filterOpen && (
        <>
          <div className="cat-drawer-overlay" onClick={() => setFilterOpen(false)} />
          <div className="cat-drawer">
            <button className="cat-drawer-close" onClick={() => setFilterOpen(false)}>&times;</button>
            <div className="cat-drawer-title">Categories</div>
            <button
              onClick={() => { setActiveCategory('all'); setFilterOpen(false); }}
              className={'cat-drawer-chip' + (activeCategory === 'all' ? ' active grad-0' : '')}
            >
              All Products
            </button>
            {dbCategories.map((cat, i) => {
              const isActive = activeCategory === cat.slug;
              const gradIdx = (i + 1) % 9;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.slug); setFilterOpen(false); }}
                  className={'cat-drawer-chip' + (isActive ? ' active grad-' + gradIdx : '')}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </>
      )}

      <section>
        <div className="container">
          {err && (
            <div className="card" style={{ borderColor: '#5b1a1a' }}>
              <div style={{ color: '#ffb3b3' }}>{err}</div>
            </div>
          )}

          {loading ? (
            <div style={{ padding: '2rem 0', color: '#999' }}>Loading...</div>
          ) : filtered.length > 0 ? (
            <div className="product-card-new-grid" style={{ display: 'grid' }}>
              {filtered.map((product, idx) => (
                <ProductCard key={product.id} product={product} index={idx} />
              ))}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', color: '#999', padding: '3rem' }}>
              No products found.
            </div>
          )}
        </div>
      </section>

      <section style={{ backgroundColor: 'var(--secondary)' }}>
        <div className="container">
          <h2 className="section-title">Why Choose Our Products</h2>
          <p className="section-subtitle">Quality, efficiency, and reliability guaranteed</p>
          <div className="grid grid-4">
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ width: '50px', height: '50px', backgroundColor: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.2rem', color: 'var(--primary)' }}>✓</div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Premium Quality</h4>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Tested and certified products</p>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ width: '50px', height: '50px', backgroundColor: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.2rem', color: 'var(--primary)' }}>⚡</div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Energy Efficient</h4>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Save up to 75% on energy costs</p>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ width: '50px', height: '50px', backgroundColor: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.2rem', color: 'var(--primary)' }}>🛡️</div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Long Warranty</h4>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Up to 5 years warranty</p>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ width: '50px', height: '50px', backgroundColor: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.2rem', color: 'var(--primary)' }}>🚚</div>
              <h4 style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Fast Delivery</h4>
              <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Quick delivery across India</p>
            </div>
          </div>
        </div>
      </section>

      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h2>Need Help Choosing?</h2>
            <p>Our lighting experts are here to help you find the perfect solution</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn">Get Expert Advice</Link>
              <Link to="/media" className="btn-secondary">View Projects</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
